import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  Column,
  ContainerGrayBottomBorder,
  FormItemContainer,
  QuestionName,
  QuestionNameAsteriskInfoRow,
  RedAsterisk,
  RedAsteriskWrapper,
} from '../QuestionRow/QuestionRow.styled';
import {
  AdditionalWrapper,
  Container as PriceWrapperContainer,
  PriceContainer,
  PriceLabel,
  PriceValue,
} from '../FormItemWrapper/FormItemWrapper.styled';
import { useBreakpointFlag } from 'utils/hooks/useBreakpointFlag';
import { Select } from 'components/Select/Select';
import { IOption } from 'components/Select/type';
import { getComponentsBasedOnDropdownType } from '../FormItem/helpers';
import { DropdownType } from '../FormItem/constants';
import { InlineSelectWrapper } from './ConfigurationQuestionRow.styled';
import { useFormatMoney } from 'utils/hooks/useFormatMoney';
import { useGetConfigurationAnswerPriceInclVAT } from 'components/SubHeader/hooks';
import { IRootReducerState } from 'store/store';
import {
  setClearedExtraPriceQuestionSlugs,
  setSelectedConfigurationAnswers,
} from 'store/SubmitConfiguration/actions/submitConfiguration';
import {
  IConfigurationQuestion,
  IConfigurationQuestionOption,
  ISelectedConfigurationAnswer,
} from 'types/ConfigurationQuestion.types';

const toSelectOption = (option: IConfigurationQuestionOption): IOption => ({
  label: option.label,
  value: option.slug,
  original_option: option,
});

interface IConfigurationQuestionRowProps {
  question: IConfigurationQuestion;
  showBorder: boolean;
}

export const ConfigurationQuestionRow = ({
  question,
  showBorder,
}: IConfigurationQuestionRowProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { isSmallTablet } = useBreakpointFlag();

  const { currency } = useSelector(
    (state: IRootReducerState) => state.shoppingCartInfo
  );
  const formatMoney = useFormatMoney(currency);

  const selectedConfigurationAnswers = useSelector(
    (state: IRootReducerState) =>
      state.submitConfigurationInfo.selectedConfigurationAnswers
  );

  const clearedExtraPriceQuestionSlugs = useSelector(
    (state: IRootReducerState) =>
      state.submitConfigurationInfo.clearedExtraPriceQuestionSlugs
  );

  const selectedAnswer = selectedConfigurationAnswers.find(
    (answer: ISelectedConfigurationAnswer) =>
      answer.questionSlug === question.slug
  );

  const selectedAnswerPriceInclVAT = useGetConfigurationAnswerPriceInclVAT(
    selectedAnswer?.price
  );

  const handleChange = (option: IOption) => {
    const chosen = option.original_option as IConfigurationQuestionOption;
    const newAnswer: ISelectedConfigurationAnswer = {
      questionSlug: question.slug,
      optionSlug: chosen.slug,
      questionLabel: question.label,
      optionLabel: chosen.label,
      price: chosen.price,
    };
    const others = selectedConfigurationAnswers.filter(
      (answer: ISelectedConfigurationAnswer) =>
        answer.questionSlug !== question.slug
    );
    dispatch(setSelectedConfigurationAnswers([...others, newAnswer]));
    // Answering again un-marks the question as cleared.
    if (clearedExtraPriceQuestionSlugs.includes(question.slug)) {
      dispatch(
        setClearedExtraPriceQuestionSlugs(
          clearedExtraPriceQuestionSlugs.filter(
            (slug: string) => slug !== question.slug
          )
        )
      );
    }
  };

  const handleClear = () => {
    dispatch(
      setSelectedConfigurationAnswers(
        selectedConfigurationAnswers.filter(
          (answer: ISelectedConfigurationAnswer) =>
            answer.questionSlug !== question.slug
        )
      )
    );
    // Remember the clear so the default isn't re-applied on the next query.
    if (!clearedExtraPriceQuestionSlugs.includes(question.slug)) {
      dispatch(
        setClearedExtraPriceQuestionSlugs([
          ...clearedExtraPriceQuestionSlugs,
          question.slug,
        ])
      );
    }
  };

  const isAddedPriceNegative =
    typeof selectedAnswerPriceInclVAT === 'number' &&
    selectedAnswerPriceInclVAT < 0;

  return (
    <ContainerGrayBottomBorder showBorder={showBorder}>
      <FormItemContainer hasValue={!!selectedAnswer} isInlineControl>
        <Column>
          <QuestionNameAsteriskInfoRow>
            <QuestionName>{question.label}</QuestionName>
            {question.is_required && !selectedAnswer && (
              <RedAsteriskWrapper>
                <RedAsterisk>*</RedAsterisk>
              </RedAsteriskWrapper>
            )}
          </QuestionNameAsteriskInfoRow>
        </Column>
        <PriceWrapperContainer isInlineControl>
          <InlineSelectWrapper>
            <Select
              key={selectedAnswer?.optionSlug || 'unanswered'}
              height={'40rem'}
              width={isSmallTablet ? '100%' : '250rem'}
              name={`configurationQuestion-${question.slug}`}
              defaultValue={
                selectedAnswer
                  ? {
                      label: selectedAnswer.optionLabel,
                      value: selectedAnswer.optionSlug,
                    }
                  : undefined
              }
              options={question.options.map(toSelectOption)}
              translate={false}
              placeholder={t('Choose')}
              isMulti={false}
              components={getComponentsBasedOnDropdownType(
                DropdownType.DEFAULT
              )}
              onChange={(option: IOption) => handleChange(option)}
              showClear={!!selectedAnswer}
              onClear={handleClear}
              pwId={`configurationQuestion-${question.slug}_clear`}
            />
          </InlineSelectWrapper>
          <AdditionalWrapper isInlineControl>
            {selectedAnswer && (
              <PriceContainer>
                <PriceLabel>
                  {isAddedPriceNegative ? t('Discount') : t('Added price')}:
                </PriceLabel>
                <PriceValue>
                  {formatMoney(selectedAnswerPriceInclVAT)}
                </PriceValue>
              </PriceContainer>
            )}
          </AdditionalWrapper>
        </PriceWrapperContainer>
      </FormItemContainer>
    </ContainerGrayBottomBorder>
  );
};
